import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { InternalSupportChatWidget } from "@/components/internal/InternalSupportChatWidget";
import {
	AppSidebar,
	SidebarTriggerButton,
} from "@/components/layout/AppSidebar";
import { InternalGlobalCommandPalette } from "@/components/layout/InternalGlobalCommandPalette";
import {
	UserProfileProvider,
	useUserProfileContext,
} from "@/contexts/UserProfileContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppLocale } from "@/lib/i18n";
import { isAllowedInternalEmail } from "@/lib/internal-email-domain";
import {
	canAccessInternalPath,
	getDefaultInternalPathForRole,
	isInternalUser,
} from "@/lib/roles";
import {
	buildRequiredTwoFactorSetupUrl,
	hasTwoFactorEnabled,
} from "@/lib/two-factor-policy";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const SIDEBAR_KEY = "internal-sidebar-open";

function getSectionTitle(pathname: string): string {
	if (pathname.startsWith("/changelog")) return "What’s new";
	if (pathname.startsWith("/roasting")) return "Roasting";
	if (pathname.startsWith("/security")) return m.nav_security();
	if (pathname.startsWith("/admin/accounting")) return m.nav_accounting();
	if (pathname.startsWith("/admin/copilot")) return m.nav_copilot();
	if (pathname.startsWith("/admin")) return m.nav_admin();
	if (pathname.startsWith("/support")) return m.nav_support();
	if (pathname.startsWith("/inventory")) return m.nav_inventory();
	if (pathname.startsWith("/logistics")) return m.nav_logistics();
	if (pathname.startsWith("/marketing")) return "Marketing";
	if (pathname.startsWith("/sales")) return m.nav_sales();
	if (pathname.startsWith("/docs")) return m.section_documents();
	return m.section_internal();
}

export function InternalLayout() {
	return (
		<UserProfileProvider>
			<InternalLayoutInner />
		</UserProfileProvider>
	);
}

function InternalLayoutInner() {
	const { locale } = useAppLocale();
	const {
		user,
		profile,
		userRole,
		isPending,
		isProfileLoading,
		isAuthenticated,
		isAuthBridgeReady,
	} = useUserProfileContext();
	const location = useLocation();
	const isMobile = useIsMobile();

	const [sidebarOpen, setSidebarOpen] = React.useState(() => {
		if (typeof window === "undefined") return true;
		const stored = localStorage.getItem(SIDEBAR_KEY);
		return stored !== null ? stored === "true" : true;
	});

	const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

	const toggleSidebar = React.useCallback(() => {
		if (isMobile) {
			setMobileSidebarOpen((v) => !v);
		} else {
			setSidebarOpen((v) => {
				const next = !v;
				localStorage.setItem(SIDEBAR_KEY, String(next));
				return next;
			});
		}
	}, [isMobile]);

	// Close mobile sidebar on navigation
	React.useEffect(() => {
		setMobileSidebarOpen(false);
	}, [location.pathname, location.search]);

	const sectionTitle = React.useMemo(
		() => getSectionTitle(location.pathname),
		[locale, location.pathname],
	);

	const twoFactorEnabled = hasTwoFactorEnabled(
		(user as { twoFactorEnabled?: boolean | null } | undefined) ?? undefined,
	);

	if (!isAuthBridgeReady || isPending || isProfileLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-muted-foreground">
					{m.layout_verifying_access()}
				</div>
			</div>
		);
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	const userEmail =
		(user as { email?: string } | undefined)?.email ??
		(profile as { email?: string } | undefined)?.email;

	if (!isAllowedInternalEmail(userEmail)) {
		return <Navigate to="/login?error=invalid-domain" replace />;
	}

	if (userRole !== undefined && !isInternalUser(userRole)) {
		return <Navigate to="/login?error=unauthorized" replace />;
	}

	if (!twoFactorEnabled && location.pathname !== "/security") {
		const redirectTarget = `${location.pathname}${location.search}`;
		return (
			<Navigate to={buildRequiredTwoFactorSetupUrl(redirectTarget)} replace />
		);
	}

	if (
		userRole !== undefined &&
		!canAccessInternalPath(userRole, location.pathname, userEmail)
	) {
		return <Navigate to={getDefaultInternalPathForRole(userRole)} replace />;
	}

	return (
		<div className="flex h-screen overflow-hidden bg-background text-foreground">
			<a
				href="#internal-main"
				className="sr-only z-50 rounded-md bg-background p-3 text-foreground focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
				onClick={(event) => {
					event.preventDefault();
					document.getElementById("internal-main")?.focus();
				}}
			>
				Skip to content
			</a>
			{/* Desktop sidebar */}
			{!isMobile && (
				<aside
					className={cn(
						"hidden shrink-0 border-r border-sidebar-border bg-sidebar transition-all duration-200 md:block",
						sidebarOpen ? "w-64" : "w-14",
					)}
				>
					<AppSidebar
						user={user}
						userRole={userRole}
						userEmail={userEmail}
						collapsed={!sidebarOpen}
					/>
				</aside>
			)}

			{/* Mobile sidebar */}
			{isMobile && mobileSidebarOpen && (
				<div className="fixed inset-0 z-50 md:hidden">
					<button
						aria-label={m.sidebar_close_navigation()}
						className="absolute inset-0 bg-black/40"
						onClick={() => setMobileSidebarOpen(false)}
					/>
					<aside className="absolute inset-y-0 left-0 w-64 border-r border-sidebar-border bg-sidebar shadow-xl">
						<AppSidebar
							user={user}
							userRole={userRole}
							userEmail={userEmail}
							onNavigate={() => setMobileSidebarOpen(false)}
						/>
					</aside>
				</div>
			)}

			{/* Main content area */}
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				<header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4">
					<SidebarTriggerButton onClick={toggleSidebar} />
					<div className="h-4 w-px bg-border" />
					<span className="text-sm font-medium">{sectionTitle}</span>
					<div className="ml-auto">
						<InternalGlobalCommandPalette />
					</div>
				</header>

				<main id="internal-main" tabIndex={-1} className="min-w-0 flex-1 overflow-auto bg-background p-6 outline-none">
					<Outlet />
				</main>
			</div>

			<InternalSupportChatWidget />
		</div>
	);
}
